<template>
    <div :key="data.widgetID">
        <data-table
            :widget="data"
            :border="border"
            can-sort-rows
            :default-sort="defaultSorting"
            :cell-class-name="getCellClassName"
            :is-multi-queue-table="true"
            :columns="getAvailableColumns"
            :editable="editable"
            :row-style="getRowStyle"
            :display-queue-as-rows="false"
            :show-columns="getVisibleColumns"
            :columns-with-percentage="getColumnsWithPercentage"
            :stripe="stripe"
            :table-data="fetchTableData"
            :widget-title="data.Title"
            @sort-change="onSortChange"
            @on-reorder-rows="onReorderRows"
            @on-update-layout="onUpdateLayout"
        >
            <template #Callers="{row, index}">
                <rule-caller-count
                    :key="index"
                    :queue-i-d="row.queue_id"
                    :queue-rule-i-d="row.queue_rule_id"
                    :queue-name="getQueueName(row.queue_id)"
                    @getCallerCount="collectCallerCount"
                />
            </template>
            <template #CurrentWaitTime="{row, index}">
                <rule-max-wait-time
                    :key="index"
                    :queue-i-d="row.queue_id"
                    :queue-rule-i-d="row.queue_rule_id"
                    :queue-name="getQueueName(row.queue_id)"
                    @getMaxWaitTime="collectCurrentWaitTime"
                />
            </template>
            <template #queue_id="{row}">
                {{ row.queue_id }}
            </template>
            <template #queue_name="{row}">
                {{ row.q_name }}
            </template>
            <template #queue_rule_name="{row}">
                {{ row.queue_rule_name }}
            </template>
            <template #All="{row, index}">
                <div v-if="isMaxWaitTimeRealTimeRow(row)">
                    <rule-max-wait-time
                        :key="index"
                        :queue-i-d="queueWithOldestCall"
                    />
                </div>
                <div v-else>
                    {{ getQueueTotals(row) }}
                </div>
            </template>
            <template #realTimeCell="{column, row, index}">
                <div v-if="isCallersRealTimeCell(column, row)">
                    <rule-caller-count
                        :key="index"
                        :queue-i-d="getColumnQueueID(column)"
                        :queue-rule-i-d="row.queue_rule_id"
                    />
                </div>
                <div v-if="isMaxWaitTimeRealTimeCell(column, row)">
                    <rule-max-wait-time
                        :key="index"
                        :queue-i-d="getColumnQueueID(column)"
                        :queue-rule-i-d="row.queue_rule_id"
                    />
                </div>
            </template>
            <template #ActiveQueueCalls="{column, row, index}">
                <active-queue-calls
                    :key="index"
                    :queue="row"
                    :all-queues="fetchTableData"
                    :queue-rule-i-d="row.queue_rule_id"
                />
            </template>
            <template #AgentsAssignedAndAvailable="{column, row, index}">
                <agents-assigned-and-available
                    :key="index"
                    :queue="row"
                    :all-queues="fetchTableData"
                    :queue-rule-i-d="row.queue_rule_id"
                />
            </template>
            <template #AgentsAssignedAndUnavailable="{column, row, index}">
                <agents-assigned-and-unavailable
                    :key="index"
                    :queue="row"
                    :all-queues="fetchTableData"
                    :queue-rule-i-d="row.queue_rule_id"
                />
            </template>
            <template #title>
                <base-widget-title :title="data.Title" />
            </template>
            <template #search-input>
                <div class="flex items-center w-48 px-1">
                    <el-input
                        v-model="filter"
                        clearable
                        :placeholder="$t('general.search')"
                        size="small"
                    >
                        <template #prefix>
                            <i class="el-input__icon vc-icon-search icon-md text-primary ml-1" />
                        </template>
                    </el-input>
                </div>
            </template>
            <template
                v-for="slot in Object.keys(queueDashboardColumnStyles)"
                #[slot]="{row}"
            >
                <div
                    :key="slot"
                    :style="getDynamicColorForColumn(slot)"
                >
                    {{ row[slot] }}
                </div>
            </template>
        </data-table>
    </div>
</template>

<script>
import get from 'lodash/get'
import cloneDeep from 'lodash/cloneDeep'
import { Option, Pagination, Select, Tooltip } from 'element-ui'
import { formatQueueDashboardsData, queueDashboardColumnStyles, } from '@/helpers/multiQueueRulesDashboard'
import minBy from 'lodash/minBy'
import dataTableMixin from '@/mixins/dataTableMixin'
import { getOptionsList } from '@/helpers/entitiesList'
import { getMinValue, mapOrder } from '@/helpers/util'
import { timeFormatter } from '@/helpers/timeFormatter'
import queueMixin from '@/mixins/queueMixin'
import widgetFilters from '@/mixins/widgetFilters'
import { getInitialTime } from '@/util/timeUtils'
import { ALL_QUEUE_ID, QUEUE_RULE_NO_RULE } from '@/enum/redesignQueueDashboardStatistics'
import {
    removeSoundsFromQueueById,
    removeSoundsFromQueueByIdAndType,
    setupSoundQueue
} from '@/composables/useSoundsStoreData'
import startCase from 'lodash/startCase'
import { allSoundList, errorSoundList, getSoundObject, warningSoundList } from '@/composables/globalData/useEntitiesData'
import { sortTableDataByDefaultList } from '@/helpers/tableSortingUtils'

export default {
    name: 'QueueRulesTable',
    mixins: [ dataTableMixin, queueMixin, widgetFilters ],
    components: {
        RuleCallerCount: () => import('./RuleCallerCount.vue'),
        RuleMaxWaitTime: () => import('./RuleMaxWaitTime.vue'),
        ActiveQueueCalls: () => import('./ActiveQueueCalls.vue'),
        AgentsAssignedAndAvailable: () => import('./AgentsAssignedAndAvailable.vue'),
        AgentsAssignedAndUnavailable: () => import('./AgentsAssignedAndUnavailable.vue'),
        DataTable: () => import('@/components/Table/DataTable.vue'),
        [Select.name]: Select,
        [Option.name]: Option,
        [Tooltip.name]: Tooltip,
        [Pagination.name]: Pagination,
    },
    props: {
        data: {
            type: Object,
            default: () => ({}),
        },
        editable: {
            type: Boolean,
            default: false,
        },
        queueRulesData: {
            type: Array,
            default: [],
        },
        tableConfigs: {
            type: Object,
            default: () => ({}),
        },
    },
    data () {
        return {
            tableData: [],
            columns: [],
            searchableFields: [],
            filter: '',
            border: true,
            stripe: false,
            widget: {},
            QUEUE_LIST_KEY: '{|queue_list|}',
            billingCdrQueueTypes: [
                'Answer',
                'Abandoned',
                'IVRExit',
                'PickUp',
                'TimeOutExit',
                'JoinEmpty',
                'LeaveEmpty',
                'JoinUnavail',
                'LeaveUnavail',
                'Full',
                'NextDestination',
            ],
            queueDashboardColumnStyles,
            callerCountData: {},
            currentWaitTimeData: {},
            allEntityQueues: [],
            waiting_state: '',
            callers_state: '',
            queueName: ''
        }
    },
    computed: {
        defaultSorting () {
            const { sortingParams } = this.data.WidgetLayout
            return sortingParams || {}
        },
        allQueueRules () {
            return this.allEntityQueues
                .reduce(
                    (acc, queue) => {
                        return [
                            ...acc,
                            ...queue.QueueRules.map(el => ({
                                ...el,
                                queue_id: queue.queue_id,
                                q_name: queue.q_name
                            })),
                            {
                                ID: QUEUE_RULE_NO_RULE,
                                RuleName: QUEUE_RULE_NO_RULE,
                                queue_id: queue.queue_id,
                                q_name: queue.q_name
                            }
                        ]
                    },
                    []
                )
        },
        fetchTableData: {
            get: function () {
                let tableData = this.tableData
                tableData.map(el => el.q_name = this.getQueueName(el.queue_id))
                tableData = this.entitiesFilter(tableData)

                if (this.filter && this.searchableFields.length > 0) {
                    tableData = tableData.filter(c => {
                        return this.searchableFields.some(field => {
                            if (c[field]) {
                                return c[field].toString().toLowerCase().includes(this.filter.toLowerCase())
                            }
                            return false
                        })
                    })
                }
                
                tableData = sortTableDataByDefaultList(tableData, this.data, 'queue_id')
                
                const sortingParams = this.data.WidgetLayout?.sortingParams
                if (sortingParams?.order !== null && sortingParams?.order !== undefined) {
                    const visibleQueuesColumns = get(this.widget.WidgetLayout, 'Columns.visibleColumns') || this.columns.map(c => c.prop)
                    tableData = mapOrder(tableData, visibleQueuesColumns, 'queue_id')
                }
                tableData.map(row => {
                    let calls = []
                    if (row.queue_id === ALL_QUEUE_ID) {
                        row.Callers = this.allQueueCalls.length
                        calls = this.allQueueCalls
                    } else {
                        const queueData = this.$store.getters['queues/filterQueuesByIds']([ row.queue_id ])
                        calls = get(queueData, '[0].Calls', [])
                        row.Callers = calls.length
                    }
                    let minJoinTimestamp = new Date().getTime() * 10000

                    if (calls.length) {
                        minJoinTimestamp = getMinValue(calls, 'JoinTimeStamp_UTC_CLIENT')
                    }
                    minJoinTimestamp = getInitialTime(minJoinTimestamp)
                    row.CurrentWaitTime = minJoinTimestamp

                    return row
                })

                return tableData
            },
            set: function (newValue) {
                return newValue
            }
        },
        columnsAreManaged () {
            return !!get(this.widget.WidgetLayout, 'Columns.visibleColumns')
        },
        getAvailableColumns () {
            return this.availableColumns
        },
        getVisibleColumns () {
            return get(this.widget.WidgetLayout, 'Columns.visibleColumns') || this.columns.map(c => c.prop)
        },
        selectedQueueRules () {
            try {
                const queueListConfig = this.data.WidgetConfig.filter((config) => config.ParameterName === this.QUEUE_LIST_KEY)
                return get(queueListConfig, '[0].WidgetParameterValueJson.EntityPositive', [])
            } catch (e) {
                return []
            }
        },
        queueWithActiveCalls () {
            return this.$store.getters['queues/queueWithActiveCalls']
        },
        queueWithOldestCall () {
            const allCalls = []
            this.queueWithActiveCalls.forEach((queue) => {
                const calls = queue.Calls || []
                calls.map((call) => {
                    call['QueueID'] = queue.QueueID
                })
                allCalls.push(...calls)
            })
            const oldestCall = minBy(allCalls, 'JoinTimeStamp')
            return get(oldestCall, 'QueueID', null)
        },
        getColumnsWithPercentage () {
            if (!this.showStatsInPercentage) {
                return []
            }
            return this.billingCdrQueueTypes
        },
        allQueueCalls () {
            return this.$store.getters['queues/allQueueCalls']
        },
        callersValue () {
            if (!this.callerCountData) return
            const callersArraySorted = Object.entries(this.callerCountData).sort((a, b) => Number(a[1]) - Number(b[1]))

            if (!callersArraySorted) return
            const lastItem = callersArraySorted.pop()
            if (!lastItem) return
            return {
                value: lastItem[1],
                queueID: lastItem[0]
            }

        },
        maxRingTimeValue () {
            if (!this.currentWaitTimeData) return
            const currentWaitTimeDataSorted = Object.entries(this.currentWaitTimeData).sort((a, b) => this.timeToSeconds(a[1]) - this.timeToSeconds(b[1]))
            if (!currentWaitTimeDataSorted) return
            const lastItem = currentWaitTimeDataSorted.pop()
            if (!lastItem) return
            return {
                value: this.timeToSeconds(lastItem[1]),
                queueID: lastItem[0]
            }
        },
        isMaximumWarningCallersValue () {
            const max = this.callersValue
            if (!max) return
            const minValFromSettings = get(this.data.WidgetLayout, 'maximumWarningCallersValue', 0)
            const maxValFromSettings = get(this.data.WidgetLayout, 'maximumLimitCallersValue', 0)

            return Number(max.value) <= Number(maxValFromSettings) && Number(max.value) >= Number(minValFromSettings)
        },
        isMaximumLimitCallersValue () {
            const max = this.callersValue
            if (!max) return
            const maxValFromSettings = get(this.data.WidgetLayout, 'maximumLimitCallersValue', 0)

            return Number(max.value) >= Number(maxValFromSettings)
        },
        isMaximumWarningMaxRingTimeValue () {
            const max = this.maxRingTimeValue
            if (!max) return
            const minValFromSettings = get(this.data.WidgetLayout, 'maximumWarningMaxRingTimeValue', 0)
            const maxValFromSettings = get(this.data.WidgetLayout, 'maximumLimitMaxRingTimeValue', 0)

            return Number(max.value) <= Number(maxValFromSettings) && Number(max.value) >= Number(minValFromSettings)
        },
        isMaximumLimitMaxRingTimeValue () {
            const max = this.maxRingTimeValue
            if (!max) return
            const maxValFromSettings = get(this.data.WidgetLayout, 'maximumLimitMaxRingTimeValue', 0)

            return Number(max.value) >= Number(maxValFromSettings)
        },
        warningSounds () {
            return warningSoundList.value
        },
        errorSounds () {
            return errorSoundList.value
        },
        allSounds () {
            return allSoundList.value
        },
        filteredQueueRules () {
            if (this.selectedQueueRules.length) {
                return this.allQueueRules.filter((queue) => this.selectedQueueRules.includes(queue.queue_id))
            } else {
                return this.allQueueRules
            }
        }
    },
    methods: {
        spanMethod ({ row, column, rowIndex, columnIndex }) {
            if ([ 'queue_id', 'queue_name' ].includes(column.property)) {
                const queueId = row.queue_id
                const queueIdGroup = this.fetchTableData.filter(item => item.queue_id === queueId)
                const firstRow = this.fetchTableData.findIndex(item => item.queue_id === queueId)
                if (rowIndex === firstRow) {
                    return [ queueIdGroup.length, 1 ]
                } else {
                    return [ 0, 0 ]
                }
            }
        },
        getQueueName (queueID) {
            if (queueID === ALL_QUEUE_ID || queueID === 'Stat type') {
                return queueID
            }

            const queue = this.allQueueRules.filter((queue) => queue.queue_id === Number(queueID))
            return get(queue, '[0].q_name', '--')
        },
        getRowStyle ({ row }) {
            const color = get(queueDashboardColumnStyles[row['Stat type']], 'color')
            const maximumWarningCallersColor = get(this.data.WidgetLayout, 'maximumWarningCallersColor', '')
            const maximumLimitCallersColor = get(this.data.WidgetLayout, 'maximumLimitCallersColor', '')
            const maximumWarningMaxRingTimeColor = get(this.data.WidgetLayout, 'maximumWarningMaxRingTimeColor', '')
            const maximumLimitMaxRingTimeColor = get(this.data.WidgetLayout, 'maximumLimitMaxRingTimeColor', '')

            return {
                '--text-col-color': color,
                ...(maximumWarningCallersColor && { '--maximum-warning-callers-color': maximumWarningCallersColor }),
                ...(maximumLimitCallersColor && { '--maximum-limit-callers-color': maximumLimitCallersColor }),
                ...(maximumWarningMaxRingTimeColor && { '--maximum-warning-max-ring-time-color': maximumWarningMaxRingTimeColor }),
                ...(maximumLimitMaxRingTimeColor && { '--maximum-limit-max-ring-time-color': maximumLimitMaxRingTimeColor })
            }
        },
        getCellClassName ({ column, row }) {
            let className = ''
            if (this.callersValue && column.property === 'Callers') {
                const minValFromSettings = get(this.data.WidgetLayout, 'maximumWarningCallersValue', 0)
                const maxValFromSettings = get(this.data.WidgetLayout, 'maximumLimitCallersValue', 0)
                const val = this.callerCountData[row.queue_id]

                if (Number(val) >= Number(maxValFromSettings)) {
                    className = 'limitCallersValue'
                } else if (Number(val) >= Number(minValFromSettings)) {
                    className = 'warningCallersValue'
                }

                return className
            }

            if (this.maxRingTimeValue && column.property === 'CurrentWaitTime' && this.currentWaitTimeData[row.queue_id] !== undefined) {
                const val = this.timeToSeconds(this.currentWaitTimeData[row.queue_id])
                const minValFromSettings = get(this.data.WidgetLayout, 'maximumWarningMaxRingTimeValue', 0)
                const maxValFromSettings = get(this.data.WidgetLayout, 'maximumLimitMaxRingTimeValue', 0)

                if (Number(val) >= Number(maxValFromSettings)) {
                    className = 'limitMaxRingTimeValue'
                } else if (Number(val) >= Number(minValFromSettings)) {
                    className = 'warningMaxRingTimeValue'
                }
                return className
            }

            if (queueDashboardColumnStyles[column.property] || queueDashboardColumnStyles[row['Stat type']]) {
                className = 'text-white'
            }

            return className
        },
        async getWidgetData () {
            try {
                if (!this.filteredQueueRules.length) {
                    return
                }

                let availableColumns = this.queueRulesData[0]

                let data = []
                const columns = []

                const displayRowWithTotals = get(this.data.WidgetLayout, 'displayRowWithTotals', true)

                const showStatsInPercentage = this.showStatsInPercentage

                removeSoundsFromQueueById(`multi-queue-${this.queueName}`)

                this.filteredQueueRules.forEach((queueRule) => {
                    let dbRuleData = this.queueRulesData.find(el => Number(el.RuleID) === Number(queueRule.ID))

                    if (!dbRuleData) {
                        dbRuleData = {}
                    }

                    const objectToAppend = {
                        AvgRingTime: 0,
                        CallCount: 0,
                        ExitCounts: [],
                        InSLACount: 0,
                        MaxRingTime: 0,
                        NotInSLACount: 0,
                        queue_id: Number(queueRule.queue_id),
                        q_name: queueRule.q_name,
                        queue_rule_id: queueRule.ID,
                        queue_rule_name: queueRule.RuleName,
                        ...dbRuleData
                    }

                    data.push(objectToAppend)
                })

                const result = formatQueueDashboardsData(data, displayRowWithTotals, false, showStatsInPercentage)

                availableColumns = result.columns
                data = result.data

                this.searchableFields = []

                for (const column in availableColumns) {
                    const columnData = {
                        prop: column,
                        fixed: false,
                        align: 'center',
                        label: this.$t(column) || startCase(column),
                    }

                    columns.push(columnData)
                    this.searchableFields.push(column)
                }

                this.columns = columns
                this.tableData = data


            } catch (e) {
                console.warn(e)
            }
        },
        isCallersRealTimeCell (column, row) {
            if ([ ALL_QUEUE_ID, 'Stat type' ].includes(column.prop)) {
                return
            }
            return row['Stat type'] === 'Callers'
        },
        isMaxWaitTimeRealTimeCell (column, row) {
            if ([ ALL_QUEUE_ID, 'Stat type' ].includes(column.prop)) {
                return
            }
            return row['Stat type'] === 'CurrentWaitTime'
        },
        getColumnQueueID (column) {
            return Number(column.prop)
        },
        isMaxWaitTimeRealTimeRow (row) {
            return row['Stat type'] === 'CurrentWaitTime'
        },
        getQueueTotals (row) {
            if (row['Stat type'] === 'Callers') {
                return this.allQueueCalls.length
            }
            return row[ALL_QUEUE_ID]
        },
        onReorderRows (data = {}) {
            const { newIndex, oldIndex } = data
            let payload = {}

            const availableQueuesColumns = get(this.widget.WidgetLayout, 'Columns.availableColumns') || this.columns
            const visibleQueuesColumns = get(this.widget.WidgetLayout, 'Columns.visibleColumns') || this.columns.map(c => c.prop)
            const statTypeIndex = visibleQueuesColumns.findIndex(el => el === 'Stat type')
            visibleQueuesColumns.splice(statTypeIndex, 1)
            const targetIndex = visibleQueuesColumns[oldIndex]

            const targetPayload = availableQueuesColumns.find(el => el.prop === targetIndex)

            if (targetPayload) {
                availableQueuesColumns.splice(oldIndex, 1)
                availableQueuesColumns.splice(newIndex, 0, targetPayload)
            }
            visibleQueuesColumns.splice(oldIndex, 1)
            visibleQueuesColumns.splice(newIndex, 0, targetIndex)
            visibleQueuesColumns.splice(statTypeIndex, 0, 'Stat type')

            payload = {
                availableQueuesColumns,
                visibleQueuesColumns,
            }

            this.onUpdateLayout(payload)
        },
        onSortChange ({ prop, order }) {
            const { sortingParams } = this.data.WidgetLayout

            if (sortingParams) {
                sortingParams.order = order
                sortingParams.prop = prop
            } else {
                this.data.WidgetLayout.sortingParams = {
                    order: order,
                    prop: prop
                }
            }

            this.$emit('on-update', this.data, true)
        },
        getDynamicColorForColumn (slot) {
            const nameOfSlot = slot.charAt(0).toLowerCase() + slot.slice(1)
            const color = this.data.WidgetLayout.colors && nameOfSlot in this.data.WidgetLayout.colors && this.data.WidgetLayout.colors[nameOfSlot] ? this.data.WidgetLayout.colors[nameOfSlot] : queueDashboardColumnStyles[slot].color
            return {
                color: color
            }
        },
        timeToSeconds (time) {
            const [ minutes, seconds ] = time.split(':')

            return (parseInt(minutes) * 60) + parseInt(seconds)
        },
        soundAnimation (type, value) {
            if ((value && Number(value) === 0) || value === undefined) {
                removeSoundsFromQueueByIdAndType(`multi-queue-${this.queueName}`, type)
                return
            }

            if (!this.widget || !this.widget.WidgetLayout) {
                return
            }

            let soundItem = null

            if (type === 'caller_warning') {
                const notificationData = {
                    notification_value: this.widget.WidgetLayout.maximumWarningCallersValue,
                    entity_name: this.queueName,
                    current_value: this.callersValue.value,
                    property: 'max warning callers'
                }
                soundItem = {
                    id: `multi-queue-${this.queueName}-caller_warning`,
                    sound: this.getCurrentSoundValue(this.widget.WidgetLayout.maximumWarningCallersSound, 'warning') || '',
                    played: false,
                    isPlaying: false,
                    type: 'info',
                    timestamp: new Date().getTime(),
                    notificationHTML: this.$t('widget.multiQueueTableHtml', { ...notificationData })
                }
            }
            if (type === 'caller_limit') {
                const notificationData = {
                    notification_value: this.widget.WidgetLayout.maximumLimitCallersValue,
                    entity_name: this.queueName,
                    current_value: this.callersValue.value,
                    type: 'limit',
                    property: 'max limit callers'
                }
                soundItem = {
                    id: `multi-queue-${this.queueName}-caller_limit`,
                    sound: this.getCurrentSoundValue(this.widget.WidgetLayout.maximumLimitCallersSound, 'limit') || '',
                    played: false,
                    isPlaying: false,
                    timestamp: new Date().getTime(),
                    notificationHTML: this.$t('widget.multiQueueTableHtml', { ...notificationData })
                }
            }
            if (type === 'wait_warning') {
                const notificationData = {
                    notification_value: timeFormatter(this.widget.WidgetLayout.maximumWarningMaxRingTimeValue),
                    entity_name: this.queueName,
                    current_value: timeFormatter(this.maxRingTimeValue.value),
                    property: 'max ring time warning'
                }
                soundItem = {
                    id: `multi-queue-${this.queueName}-wait_warning`,
                    sound: this.getCurrentSoundValue(this.widget.WidgetLayout.maximumWarningMaxRingTimeSound, 'warning') || '',
                    played: false,
                    isPlaying: false,
                    type: 'info',
                    timestamp: new Date().getTime(),
                    notificationHTML: this.$t('widget.multiQueueTableHtml', { ...notificationData })
                }
            }
            if (type === 'wait_limit') {
                const notificationData = {
                    notification_value: timeFormatter(this.widget.WidgetLayout.maximumLimitMaxRingTimeValue),
                    entity_name: this.queueName,
                    current_value: timeFormatter(this.maxRingTimeValue.value),
                    property: 'max ring time limit'
                }
                soundItem = {
                    id: `multi-queue-${this.queueName}-wait_limit`,
                    sound: this.getCurrentSoundValue(this.widget.WidgetLayout.maximumLimitMaxRingTimeSound, 'limit') || '',
                    played: false,
                    isPlaying: false,
                    type: 'limit',
                    timestamp: new Date().getTime(),
                    notificationHTML: this.$t('widget.multiQueueTableHtml', { ...notificationData })
                }
            }
            if (this.widget.WidgetLayout.backQueueSLASound !== 'none' && type === 'idle' && this.waiting_state === 'idle'  && this.callers_state === 'idle') {
                soundItem = {
                    id: `multi-queue-${this.queueName}-idle`,
                    sound: this.getCurrentSoundValue(this.widget.WidgetLayout.backQueueSLASound, 'idle') || '',
                    played: false,
                    isPlaying: false,
                    type: 'idle',
                    timestamp: new Date().getTime(),
                    notificationHTML: this.$t('widget.allMultiQueueBackToIdle')
                }
            }

            if (soundItem && soundItem.sound) {
                setupSoundQueue(soundItem)
            }
        },
        collectCallerCount ({ queueID, value, queueName }) {
            this.callerCountData = {
                ...this.callerCountData,
                [queueID]: value
            }
            if (this.queueName !== queueName) {
                this.queueName = queueName
            }
            if (value === 0) {
                removeSoundsFromQueueById(`multi-queue-${this.queueName}`)
            }
        },
        collectCurrentWaitTime ({ queueID, value, queueName }) {
            this.currentWaitTimeData = {
                ...this.currentWaitTimeData,
                [queueID]: value
            }
            if (this.queueName !== queueName) {
                this.queueName = queueName
            }
            if (value === 0) {
                removeSoundsFromQueueById(`multi-queue-${this.queueName}`)
            }
        },
        getCurrentSoundValue (soundValue, type) {
            if (!soundValue) return
            let sounds = ''
            if (type === 'idle') {
                sounds = this.allSounds
            } else if (type === 'warning') {
                sounds = this.warningSounds
            } else {
                sounds = this.errorSounds
            }
            if (!sounds) null
            const currentSound = getSoundObject(sounds, soundValue)
            if (!currentSound) {
                return null
            }
            return currentSound.Rec_Path
        },
    },
    mounted () {
        if (!this.data.WidgetLayout.hasOwnProperty('displayQueuesAsRow')) {
            this.$set(this.data.WidgetLayout, 'displayQueuesAsRow', true)
        }
        if (!this.data.WidgetLayout.hasOwnProperty('displayRowWithTotals')) {
            this.$set(this.data.WidgetLayout, 'displayRowWithTotals', true)
        }
        if (!this.data.WidgetLayout.hasOwnProperty('showStatsInPercentage')) {
            this.$set(this.data.WidgetLayout, 'showStatsInPercentage', true)
        }
    },
    async created () {
        this.allEntityQueues = await getOptionsList(`${this.QUEUE_LIST_KEY}`)
    },
    watch: {
        data: {
            immediate: true,
            deep: true,
            handler (data) {
                this.widget = cloneDeep(data)
                this.getWidgetData()
            },
        },
        queuesData: {
            immediate: true,
            deep: true,
            handler () {
                this.getWidgetData()
            },
        },
        isMaximumWarningCallersValue: {
            immediate: true,
            handler (val) {
                if (val === undefined) return
                if (val) {
                    this.callers_state = 'warning'
                } else if (!val) {
                    this.callers_state = 'idle'
                }
            }
        },
        isMaximumLimitCallersValue: {
            immediate: true,
            handler (val) {
                if (val === undefined) return
                if (val) {
                    this.callers_state = 'limit'
                } else if (!val) {
                    this.callers_state = 'idle'
                }
            }
        },
        isMaximumWarningMaxRingTimeValue: {
            immediate: true,
            handler (val) {
                if (val === undefined) return
                if (val) {
                    this.waiting_state = 'warning'
                } else if (!val) {
                    this.waiting_state = 'idle'
                }
            }
        },
        isMaximumLimitMaxRingTimeValue: {
            immediate: true,
            handler (val) {
                if (val === undefined) return
                if (val) {
                    this.waiting_state = 'limit'
                } else if (!val) {
                    this.waiting_state = 'idle'
                }
            }
        },
        callers_state: {
            immediate: true,
            handler (newVal, oldVal) {
                const max = this.callersValue
                if (!max) return
                if (oldVal !== undefined && oldVal !== '' && newVal === 'idle' && this.waiting_state === 'idle') {
                    this.soundAnimation('idle')
                }
                if (newVal && newVal === 'limit') {
                    this.soundAnimation('caller_limit', max.value)
                }
                if (newVal && newVal === 'warning') {
                    this.soundAnimation('caller_warning', max.value)
                }
            }
        },
        waiting_state: {
            immediate: true,
            handler (newVal, oldVal) {
                const max = this.maxRingTimeValue
                if (!max) return
                if (oldVal !== undefined && oldVal !== '' && newVal === 'idle' && this.callers_state === 'idle') {
                    this.soundAnimation('idle')
                }
                if (newVal && newVal === 'limit') {
                    this.soundAnimation('wait_limit', max.value)
                }
                if (newVal && newVal === 'warning') {
                    this.soundAnimation('wait_warning', max.value)
                }
            }
        }
    },
    beforeUnmount () {
        removeSoundsFromQueueById(`multi-queue-${this.queueName}`)
    },
}
</script>
<style lang="scss">
td.text-white > .cell {
    color: white;
}
</style>
<style lang="scss" scoped>
::v-deep .el-input__inner::placeholder {
    @apply text-gray-500 font-medium text-base pl-1;
}
::v-deep .el-input__inner {
    @apply text-black font-medium text-base;
}
::v-deep .el-table .el-table__cell {
    padding: 6px 0px;
}
::v-deep .el-table thead th .cell {
    line-height: 15px;
    color: #6e6d6d;
    @apply flex;
}

::v-deep .el-table thead th .cell {
    line-height: 15px;
    color: #6e6d6d;
    @apply flex;
}

::v-deep .el-table .cell div {
    color: var(--text-col-color);
}

@keyframes color_bg_warning_maxRing {
  to { background-color: var(--maximum-warning-max-ring-time-color); }
}

@-webkit-keyframes color_bg_warning_maxRing {
    to { background-color: var(--maximum-warning-max-ring-time-color); }
}

@keyframes color_bg_warning_callers {
  to { background-color: var(--maximum-warning-callers-color); }
}

@-webkit-keyframes color_bg_warning_callers {
    to { background-color: var(--maximum-warning-callers-color); }
}

@keyframes color_bg_limit_maxRing {
  to { background-color: var(--maximum-limit-max-ring-time-color); }
}

@-webkit-keyframes color_bg_limit_maxRing {
    to { background-color: var(--maximum-limit-max-ring-time-color); }
}

@keyframes color_bg_limit_callers {
  to { background-color: var(--maximum-limit-callers-color); }
}

@-webkit-keyframes color_bg_limit_callers {
    to { background-color: var(--maximum-limit-callers-color); }
}

::v-deep .el-table td.el-table__cell.warningMaxRingTimeValue {
    -webkit-animation: color_bg_warning_maxRing 1.5s infinite;
    -moz-animation: color_bg_warning_maxRing 1.5s infinite;
    -ms-animation: color_bg_warning_maxRing 1.5s infinite;
    -o-animation: color_bg_warning_maxRing 1.5s infinite;
    animation: color_bg_warning_maxRing 1.5s infinite;
}
::v-deep .el-table td.el-table__cell.warningCallersValue {
    -webkit-animation: color_bg_warning_callers 1.5s infinite;
    -moz-animation: color_bg_warning_callers 1.5s infinite;
    -ms-animation: color_bg_warning_callers 1.5s infinite;
    -o-animation: color_bg_warning_callers 1.5s infinite;
    animation: color_bg_warning_callers 1.5s infinite;
}
::v-deep .el-table td.el-table__cell.limitMaxRingTimeValue {
    -webkit-animation: color_bg_limit_maxRing 1.5s infinite alternate;
    -moz-animation: color_bg_limit_maxRing 1.5s infinite alternate;
    -ms-animation: color_bg_limit_maxRing 1.5s infinite alternate;
    -o-animation: color_bg_limit_maxRing 1.5s infinite alternate;
    animation: color_bg_limit_maxRing 1.5s infinite alternate;
}
::v-deep .el-table td.el-table__cell.limitCallersValue {
    -webkit-animation: color_bg_limit_callers 1.5s infinite;
    -moz-animation: color_bg_limit_callers 1.5s infinite;
    -ms-animation: color_bg_limit_callers 1.5s infinite;
    -o-animation: color_bg_limit_callers 1.5s infinite;
    animation: color_bg_limit_callers 1.5s infinite;
}
</style>
